import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Modal } from '@/components/common/Modal'
import { Field } from '@/components/common/Field'
import { inputClass, primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { lookupSecurity } from '@/lib/marketDataApi'
import { createPosition } from '@/lib/positionsApi'
import type { Account } from '@/types/account'
import type { AssetType } from '@/types/position'

interface AddPositionModalProps {
  open: boolean
  onClose: () => void
  accounts: Account[]
  onNeedAccount: () => void
}

const TODAY = new Date().toISOString().slice(0, 10)

export function AddPositionModal({
  open,
  onClose,
  accounts,
  onNeedAccount,
}: AddPositionModalProps) {
  const queryClient = useQueryClient()
  const [accountId, setAccountId] = useState<number | null>(accounts[0]?.id ?? null)

  // Wenn accounts nach dem Initial-Mount geladen werden, accountId nachziehen
  useEffect(() => {
    if (accountId === null && accounts.length > 0) {
      setAccountId(accounts[0].id)
    }
  }, [accounts, accountId])
  const [isin, setIsin] = useState('')
  const [ticker, setTicker] = useState('')
  const [name, setName] = useState('')
  const [assetType, setAssetType] = useState<AssetType>('etf')
  const [currency, setCurrency] = useState('USD')
  const [purchaseDate, setPurchaseDate] = useState(TODAY)
  const [quantity, setQuantity] = useState('')
  const [pricePerUnit, setPricePerUnit] = useState('')
  const [fees, setFees] = useState('0')
  const [error, setError] = useState<string | null>(null)
  const [lookupPending, setLookupPending] = useState(false)
  const [lookupHint, setLookupHint] = useState<string | null>(null)

  const handleIsinBlur = async () => {
    const cleaned = isin.trim().toUpperCase()
    if (cleaned.length !== 12) return
    setLookupPending(true)
    setLookupHint(null)
    try {
      const result = await lookupSecurity(cleaned)
      if (result === null) {
        setLookupHint('Nicht gefunden — bitte manuell eingeben.')
        return
      }
      if (!name.trim()) setName(result.name)
      if (!ticker.trim() && result.symbol) setTicker(result.symbol)
      if (result.currency) setCurrency(result.currency)
      setLookupHint(`Gefunden: ${result.name}${result.currency ? ` (${result.currency})` : ''}`)
    } catch {
      setLookupHint('Lookup fehlgeschlagen.')
    } finally {
      setLookupPending(false)
    }
  }

  const reset = () => {
    setIsin('')
    setTicker('')
    setName('')
    setQuantity('')
    setPricePerUnit('')
    setFees('0')
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: createPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
      reset()
      onClose()
    },
    onError: (err) => {
      if (!isAxiosError(err)) {
        setError('Server nicht erreichbar.')
        return
      }
      const status = err.response?.status
      if (status === 409) {
        setError('ISIN existiert bereits auf diesem Account.')
      } else if (status === 404) {
        setError('Account nicht gefunden.')
      } else if (status === 422) {
        setError('Eingaben prüfen: ISIN (Prüfziffer), Datum, Beträge.')
      } else {
        setError('Unerwarteter Fehler.')
      }
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (accountId === null) return
    setError(null)
    mutation.mutate({
      account_id: accountId,
      isin: isin.trim().toUpperCase(),
      ticker: ticker.trim() || null,
      name: name.trim(),
      asset_type: assetType,
      currency: currency.trim().toUpperCase(),
      initial_tax_lot: {
        purchase_date: purchaseDate,
        quantity,
        price_per_unit: pricePerUnit,
        fees: fees || '0',
      },
    })
  }

  const canSubmit =
    accountId !== null &&
    isin.trim().length === 12 &&
    name.trim().length > 0 &&
    quantity.length > 0 &&
    pricePerUnit.length > 0 &&
    !mutation.isPending

  if (accounts.length === 0) {
    return (
      <Modal open={open} onClose={onClose} title="Neue Position">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Noch kein Broker-Account vorhanden. Lege zuerst einen Account an, bevor du Positionen
            eingibst.
          </p>
          <button type="button" onClick={onNeedAccount} className={primaryButtonClass}>
            → Account anlegen
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open={open} onClose={onClose} title="Neue Position">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <Field label="Account">
          <select
            value={accountId ?? ''}
            onChange={(e) => setAccountId(Number(e.target.value))}
            className={selectClass}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.broker_type} ({a.currency})
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Field label="ISIN · 12 Zeichen">
              <input
                value={isin}
                onChange={(e) => {
                  setIsin(e.target.value.toUpperCase())
                  setLookupHint(null)
                }}
                onBlur={handleIsinBlur}
                maxLength={12}
                minLength={12}
                required
                placeholder="US0378331005"
                className={`${inputClass} font-mono`}
              />
            </Field>
            {lookupPending && <p className="font-mono text-[10px] text-faint">Suche…</p>}
            {lookupHint && !lookupPending && (
              <p className="font-mono text-[10px] text-muted">{lookupHint}</p>
            )}
          </div>
          <Field label="Ticker">
            <input
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              maxLength={16}
              placeholder="AAPL"
              className={`${inputClass} font-mono`}
            />
          </Field>
        </div>

        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={128}
            placeholder="Apple Inc."
            className={inputClass}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Asset-Type">
            <select
              value={assetType}
              onChange={(e) => setAssetType(e.target.value as AssetType)}
              className={selectClass}
            >
              <option value="etf">ETF</option>
              <option value="stock">Aktie</option>
              <option value="savings_plan">Sparplan</option>
            </select>
          </Field>
          <Field label="Currency">
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              minLength={3}
              required
              className={`${inputClass} font-mono`}
            />
          </Field>
        </div>

        <div className="hairline-t pt-5">
          <p className="tracked-caps mb-4 text-[10px] text-faint">Erste Tranche</p>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Kaufdatum">
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                max={TODAY}
                required
                className={`${inputClass} font-mono`}
              />
            </Field>
            <Field label="Stück">
              <input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                inputMode="decimal"
                placeholder="10"
                className={`${inputClass} font-mono`}
              />
            </Field>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <Field label="Preis / Stück">
              <input
                value={pricePerUnit}
                onChange={(e) => setPricePerUnit(e.target.value)}
                required
                inputMode="decimal"
                placeholder="180.50"
                className={`${inputClass} font-mono`}
              />
            </Field>
            <Field label="Gebühren">
              <input
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                inputMode="decimal"
                className={`${inputClass} font-mono`}
              />
            </Field>
          </div>
        </div>

        {error && (
          <p role="alert" className="font-mono text-xs text-loss">
            {error}
          </p>
        )}

        <button type="submit" disabled={!canSubmit} className={primaryButtonClass}>
          {mutation.isPending ? 'Speichert' : 'Position anlegen'}
        </button>
      </form>
    </Modal>
  )
}
